import React from 'react';
import { Box, Tooltip, Typography } from '@mui/material';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import VerifiedOutlinedIcon from '@mui/icons-material/VerifiedOutlined';
import BlockOutlinedIcon from '@mui/icons-material/BlockOutlined';
import { KpiEvidence } from '../../services/benchmarkService';
import { INDICATIVE_CASE_THRESHOLD, evidenceCases, evidenceLabel, isIndicative } from './benchmarkEvidence';
import { Qualification } from './withheldMetrics';
import { count } from './BenchmarkVocabulary';
import { dsVar, ink } from '../../theme/tokens';

/**
 * The caveats a run attaches to its own figures: how much evidence stands
 * behind them, and what it declined to measure or measured with a condition.
 *
 * These live beside the figures rather than in a footnote, because on a small
 * or qualified run the caveat is the most important thing on the panel.
 */

const chipSx = (color: string) => ({
    display: 'inline-flex',
    alignItems: 'center',
    gap: 0.5,
    px: 1,
    py: 0.25,
    borderRadius: '999px',
    fontSize: '0.6875rem',
    fontWeight: 600,
    whiteSpace: 'nowrap' as const,
    color: ink(color),
    backgroundColor: `${color}1A`,
    border: `1px solid ${color}4D`,
    cursor: 'help'
});

/** "8 cases · low confidence", amber when that is too little to go on. */
export const EvidenceChip: React.FC<{ evidence?: KpiEvidence | null }> = ({ evidence }) => {
    const label = evidenceLabel(evidence);
    if (!label) return null;
    const weak = isIndicative(evidence);
    return (
        <Tooltip
            arrow
            title={weak
                ? `Figures from fewer than ${count(INDICATIVE_CASE_THRESHOLD)} cases, or marked low-confidence by the run, are indicative only.`
                : 'The number of cases behind these figures, and the run\'s own confidence in them.'}
        >
            <Box component="span" sx={chipSx(weak ? '#FBBF24' : '#94A3B8')}>{label}</Box>
        </Tooltip>
    );
};

/** A "Qualified" badge whose tooltip gives each caveat and the run's reason. */
export const QualifiedBadge: React.FC<{ qualifications: Qualification[] }> = ({ qualifications }) => {
    if (!qualifications.length) return null;
    return (
        <Tooltip
            arrow
            title={
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, py: 0.5 }}>
                    {qualifications.map((q) => (
                        <Box key={q.source}>
                            <Typography sx={{ fontSize: '0.75rem', fontWeight: 600 }}>{q.summary}</Typography>
                            {q.summary !== q.reason && (
                                <Typography sx={{ fontSize: '0.6875rem', opacity: 0.8, mt: 0.25 }}>{q.reason}</Typography>
                            )}
                        </Box>
                    ))}
                </Box>
            }
        >
            <Box component="span" sx={chipSx('#FBBF24')}>
                <WarningAmberOutlinedIcon sx={{ fontSize: 12 }} />
                Qualified
            </Box>
        </Tooltip>
    );
};

/** A caveat the run lifted — e.g. every customer's KYC predates their alerts. */
export const PositiveBadge: React.FC<{ label: string; detail: string }> = ({ label, detail }) => (
    <Tooltip arrow title={detail}>
        <Box component="span" sx={chipSx('#10B981')}>
            <VerifiedOutlinedIcon sx={{ fontSize: 12 }} />
            {label}
        </Box>
    </Tooltip>
);

/**
 * Stands in for figures the run refused to compute.
 *
 * Distinct from "not computed": the run had the data and declined, for a
 * stated reason, because the number would have been misleading.
 */
export const WithheldNotice: React.FC<{ reasons: string[]; compact?: boolean }> = ({ reasons, compact }) => (
    <Box
        sx={{
            p: compact ? 1.5 : 2.5,
            borderRadius: '8px',
            border: '1px dashed rgba(251, 191, 36, 0.35)',
            backgroundColor: 'rgba(251, 191, 36, 0.05)'
        }}
    >
        <Box className="flex items-center gap-1.5">
            <BlockOutlinedIcon sx={{ fontSize: 16, color: ink('#FBBF24') }} />
            <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600, color: ink('#FCD34D') }}>
                Withheld for this run
            </Typography>
        </Box>
        {[...new Set(reasons)].map((reason) => (
            <Typography key={reason} sx={{ fontSize: '0.75rem', color: dsVar('fg-2'), mt: 0.75, lineHeight: 1.6 }}>
                {reason}
            </Typography>
        ))}
    </Box>
);

/**
 * Says, once and at the top, that the figures below rest on too few cases to
 * be read as a result.
 */
export const IndicativeBanner: React.FC<{ evidence?: KpiEvidence | null }> = ({ evidence }) => {
    if (!isIndicative(evidence)) return null;
    const n = evidenceCases(evidence);
    return (
        <Box
            sx={{
                p: 2,
                borderRadius: '10px',
                border: '1px solid rgba(251, 191, 36, 0.35)',
                backgroundColor: 'rgba(251, 191, 36, 0.06)',
                display: 'flex',
                gap: 1.5,
                alignItems: 'flex-start'
            }}
        >
            <WarningAmberOutlinedIcon sx={{ color: ink('#FBBF24'), fontSize: 20, mt: '1px' }} />
            <Box>
                <Typography sx={{ fontSize: '0.875rem', fontWeight: 700, color: ink('#FCD34D') }}>
                    Indicative only{n !== null ? `: ${count(n)} ${n === 1 ? 'case' : 'cases'}` : ''}
                    {evidence?.confidence ? `, ${evidence.confidence} confidence` : ''}
                </Typography>
                <Typography sx={{ fontSize: '0.8125rem', color: dsVar('fg-soft'), mt: 0.5, lineHeight: 1.6 }}>
                    This run scored too few cases for its figures to be read as a result. Use it to check how
                    the agent handles these alerts, not to size a change to your triage. Staffing estimates are
                    not shown for a sample this small.
                </Typography>
            </Box>
        </Box>
    );
};
